import type { GenericHighlight, MixedHighlightValue } from "@/types/Highlights";
import type {
  CustomHighlightItem,
  HighlightItem,
  PremadeHighlightItem,
} from "@/features/experience-shared/highlights-v2/types/highlight-item";

/**
 * Edge adapter between the simplified UI model (`HighlightItem[]`) and the persisted
 * `MixedHighlightValue`. Lives only at the store boundary.
 *
 * RAW-only simplification: the RAW save path
 * (`useRawContentSaveOperation.ts` + `lib/map-*-highlights.ts`) only distinguishes
 * `action !== "DELETE"` (keep vs drop) plus `visualization_order` (ordering). The
 * remaining action tags (CREATE/EDIT/REMOVE/NOOP) only feed the curation/translation
 * diff UI, which is out of scope here. So this adapter is a straight projection, NOT a
 * diff reconstruction: it does not rebuild action tags. If these components are ever
 * reused for curation/translation, the adapter must be extended to reproduce full diff
 * semantics.
 *
 * Round-trip determinism is a hard requirement: `toManageable(fromManageable(v))` must be
 * deep-equal to `v` for any value the UI did not touch, so the field store's `hasChanges`
 * does not spuriously flip. That means preserving the existing `id`/`code` rather than
 * regenerating them, and preserving `visualization_order` for untouched items.
 */

/** Items with this action are soft-deleted; they never enter the UI list. */
const DELETED_ACTION = "DELETE";

/**
 * Persisted premade lists are built by mapping stored codes through masterdata lookups
 * (`getHighlightByCode`), which returns `undefined` for a code that is not in masterdata —
 * so the incoming array can legitimately be sparse. Those holes carry no displayable data
 * and cannot round-trip, so they are dropped here instead of crashing the projection.
 */
function isPresent(item: GenericHighlight | undefined | null): item is GenericHighlight {
  return item != null;
}

function isVisible(item: GenericHighlight): boolean {
  return item.action !== DELETED_ACTION;
}

function byVisualizationOrder(a: GenericHighlight, b: GenericHighlight): number {
  return (a.visualization_order ?? 0) - (b.visualization_order ?? 0);
}

function toPremadeItem(item: GenericHighlight): PremadeHighlightItem {
  if (item.code == null) {
    throw new Error(`Premade highlight "${item.id}" is missing a code.`);
  }

  return {
    kind: "premade",
    id: item.id,
    name: item.name,
    code: item.code,
  };
}

function toCustomItem(item: GenericHighlight): CustomHighlightItem {
  // Unlike premade (a masterdata reference whose code is mandatory), a persisted custom
  // highlight may legitimately have no code — the RAW save path never reads it. So preserve
  // the code as-is instead of throwing, omitting the key entirely when it is absent so a
  // codeless record round-trips byte-identically (no fabricated `code: undefined`).
  const base = { kind: "custom", id: item.id, name: item.name } as const;
  return item.code == null ? base : { ...base, code: item.code };
}

/**
 * Persisted model -> UI model.
 * Drops soft-deleted items and sorts by `visualization_order` so the array index becomes
 * the source of truth for order.
 */
export function fromManageable(value: MixedHighlightValue): HighlightItem[] {
  const premade = [...value.premade].filter(isPresent).filter(isVisible).sort(byVisualizationOrder).map(toPremadeItem);

  const custom = [...value.custom].filter(isPresent).filter(isVisible).sort(byVisualizationOrder).map(toCustomItem);

  return [...premade, ...custom];
}

function toManageablePremade(item: PremadeHighlightItem, index: number): GenericHighlight {
  return {
    id: item.id,
    name: item.name,
    code: item.code,
    visualization_order: index + 1,
  };
}

function toManageableCustom(item: CustomHighlightItem, index: number): GenericHighlight {
  // Only emit `code` when the item actually has one. A codeless custom highlight must not gain
  // a `code: undefined` key, or the normalizer round-trip would differ from the loaded value
  // and spuriously flip `hasChanges`.
  const base: GenericHighlight = {
    id: item.id,
    name: item.name,
    visualization_order: index + 1,
  };
  return item.code == null ? base : { ...base, code: item.code };
}

/**
 * UI model -> persisted model.
 * Splits by `kind`, assigning `visualization_order` from the array index within each list.
 * Removed items are simply absent from `items` and therefore omitted from the output,
 * which matches how the RAW save mappers treat non-persisted items.
 */
export function toManageable(items: HighlightItem[]): MixedHighlightValue {
  const premade: GenericHighlight[] = [];
  const custom: GenericHighlight[] = [];

  for (const item of items) {
    if (item.kind === "premade") {
      premade.push(toManageablePremade(item, premade.length));
    } else {
      custom.push(toManageableCustom(item, custom.length));
    }
  }

  return { premade, custom };
}

/**
 * Normalizes a `MixedHighlightValue` to the canonical shape used for change detection.
 *
 * Pass this as the `normalizer` option to `useComplexModelModelWithChanges` in the field
 * store (same pattern as the media gallery's `normalizeImage`). Change detection then
 * ignores everything the UI does not own: soft-deleted (`action: "DELETE"`) items, ordering
 * gaps, action tags, and the extra masterdata fields premade items carry when loaded
 * (`hierarchical_group_code`, `archived`, `description`, `language_code`, ...).
 *
 * Defined as the adapter round-trip so the normalized snapshot is, by construction,
 * byte-identical to what `RawHighlights` writes back via `toManageable` — they can never
 * drift apart. Without this, the store's initial value (rich masterdata objects) would never
 * deep-equal the adapter output and every field would mount as dirty.
 */
export function normalizeMixedHighlight(value: MixedHighlightValue): MixedHighlightValue {
  return toManageable(fromManageable(value));
}
